import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Phone, Mail, AlertTriangle } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise, date } from '@/lib/format';
import {
  Avatar, Badge, Button, Card, Checkbox, EmptyState, Field, Input, LoadingBlock, Modal,
  PageHeader, SearchInput, Select, Table, TableWrap, Td, Textarea, Th, useToast, statusTone,
} from '@/components/ui';
import type { Tenant, Property, TenantStatus, ContactPreference } from '@shared/types';

const STATUSES: TenantStatus[] = ['active', 'applicant', 'notice', 'past', 'blacklisted'];

export default function Tenants() {
  const { currency, can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [propertyId, setPropertyId] = useState('');
  const [creating, setCreating] = useState(false);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data, loading, reload } = useApi<Tenant[]>('/tenants', {
    q: search || undefined,
    status: status || undefined,
    property_id: propertyId || undefined,
  });

  const tenants = data ?? [];
  const inArrears = tenants.filter((t) => (t.overdue_cents ?? 0) > 0).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenants"
        subtitle={
          inArrears > 0
            ? `${tenants.length} tenants · ${inArrears} in arrears`
            : `${tenants.length} tenant${tenants.length === 1 ? '' : 's'}`
        }
        action={can('staff') && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Add tenant
          </Button>
        )}
      />

      <div className="flex flex-wrap gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, phone or email…"
          className="flex-1 min-w-[240px]"
        />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
        </Select>
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : tenants.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No tenants found"
            message={search ? 'Try a different search.' : 'Add a tenant, then assign them to a room.'}
            action={can('staff') && !search && (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>Add tenant</Button>
            )}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Tenant</Th>
                  <Th>Contact</Th>
                  <Th>Where</Th>
                  <Th align="right">Rent</Th>
                  <Th align="right">Balance</Th>
                  <Th>Next due</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const overdue = (t.overdue_cents ?? 0) > 0;
                  return (
                    <tr key={t.id} className="row-hover">
                      <Td>
                        <Link to={`/app/tenants/${t.id}`} className="flex items-center gap-3 min-w-0 group">
                          <Avatar name={t.full_name} size="sm" />
                          <div className="min-w-0">
                            <p className="font-medium truncate group-hover:text-brand transition-colors">
                              {t.full_name}
                            </p>
                            {t.occupation && <p className="text-xs text-faint truncate">{t.occupation}</p>}
                          </div>
                        </Link>
                      </Td>
                      <Td>
                        <div className="text-xs space-y-0.5">
                          <a href={`tel:${t.phone}`} className="flex items-center gap-1.5 text-muted hover:text-ink">
                            <Phone className="h-3 w-3 shrink-0" />{t.phone}
                          </a>
                          {t.email && (
                            <a href={`mailto:${t.email}`} className="flex items-center gap-1.5 text-faint hover:text-ink truncate">
                              <Mail className="h-3 w-3 shrink-0" /><span className="truncate">{t.email}</span>
                            </a>
                          )}
                        </div>
                      </Td>
                      <Td className="text-muted">
                        {t.property_name ? (
                          <span className="text-sm">
                            {t.room_name ?? 'Whole property'}
                            <span className="text-faint block text-xs">{t.property_name}</span>
                          </span>
                        ) : (
                          <span className="text-faint text-sm">Unassigned</span>
                        )}
                      </Td>
                      <Td align="right" className="tnum whitespace-nowrap">
                        {t.rent_cents ? money(t.rent_cents, currency) : '—'}
                      </Td>
                      <Td align="right" className="whitespace-nowrap">
                        {(t.outstanding_cents ?? 0) > 0 ? (
                          <span className={`font-semibold tnum ${overdue ? 'text-danger' : 'text-warn'}`}>
                            {money(t.outstanding_cents ?? 0, currency)}
                          </span>
                        ) : (
                          <span className="text-ok text-sm font-medium">Clear</span>
                        )}
                        {(t.pending_cents ?? 0) > 0 && (
                          <span className="block text-xs text-faint tnum">
                            {money(t.pending_cents ?? 0, currency)} in review
                          </span>
                        )}
                      </Td>
                      <Td className="text-muted text-sm whitespace-nowrap">
                        {overdue ? (
                          <span className="inline-flex items-center gap-1 text-danger">
                            <AlertTriangle className="h-3.5 w-3.5" /> Overdue
                          </span>
                        ) : (
                          date(t.next_due_date)
                        )}
                      </Td>
                      <Td>
                        <Badge tone={statusTone(t.status)} dot>{humanise(t.status)}</Badge>
                        {/*
                          Flagged, not chased. The reminder screen is where the
                          decision to actually ask them is made.
                        */}
                        {(t.missing_documents?.length ?? 0) > 0 && (
                          <Badge tone="warn" className="ml-1.5">
                            {t.missing_documents!.length} doc{t.missing_documents!.length === 1 ? '' : 's'} missing
                          </Badge>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {creating && (
        <TenantFormModal
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

export function TenantFormModal({ tenant, onClose, onDone }: {
  tenant?: Tenant; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    full_name: tenant?.full_name ?? '',
    phone: tenant?.phone ?? '',
    email: tenant?.email ?? '',
    alt_phone: tenant?.alt_phone ?? '',
    date_of_birth: tenant?.date_of_birth?.slice(0, 10) ?? '',
    occupation: tenant?.occupation ?? '',
    employer: tenant?.employer ?? '',
    id_type: tenant?.id_type ?? 'National ID',
    id_number: tenant?.id_number ?? '',
    emergency_name: tenant?.emergency_name ?? '',
    emergency_phone: tenant?.emergency_phone ?? '',
    emergency_relation: tenant?.emergency_relation ?? '',
    status: tenant?.status ?? ('active' as TenantStatus),
    notes: tenant?.notes ?? '',
    contact_preference: tenant?.contact_preference ?? ('both' as ContactPreference),
  });
  const [portalEnabled, setPortalEnabled] = useState(tenant?.portal_enabled ?? true);
  /*
   * On by default when adding somebody. A portal nobody was told about is a
   * portal nobody uses — and the tenants who most need it are exactly the ones
   * who will never be told about it any other way. Off when editing: nobody
   * expects correcting a spelling to send a text.
   */
  const [sendInvite, setSendInvite] = useState(!tenant);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit() {
    if (!form.full_name.trim()) { toast.error('Enter the tenant\'s name'); return; }
    if (!form.phone.trim()) { toast.error('A phone number is required — it is their portal login'); return; }

    setBusy(true);
    try {
      const payload = { ...form, portal_enabled: portalEnabled, send_invite: sendInvite };
      if (tenant) {
        await api.patch(`/tenants/${tenant.id}`, payload);
        toast.success('Tenant updated');
      } else {
        const created = await api.post('/tenants', payload);
        /*
         * Say what actually happened rather than "saved". An invitation that
         * silently went nowhere — no email address, texting not switched on —
         * is worth knowing about now, not when the tenant says they never
         * received anything.
         */
        if (!sendInvite) {
          toast.success('Tenant added');
        } else if (created?.invite?.sent) {
          toast.success('Tenant added and invited to the portal');
        } else {
          toast.info(
            `Tenant added, but the invitation did not go out: ${
              (created?.invite?.reasons ?? []).join('; ') || 'no contact details on file'
            }`,
          );
        }
      }
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the tenant');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={tenant ? 'Edit tenant' : 'Add a tenant'}
      subtitle="Assign them to a room afterwards to create a lease"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="label mb-3">Identity & contact</p>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <Input value={form.full_name} onChange={set('full_name')} placeholder="Amara Okafor" autoFocus />
              </Field>
              <Field label="Phone" hint="Their portal login. Include country code." required>
                <Input value={form.phone} onChange={set('phone')} placeholder="+254712345678" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email">
                <Input type="email" value={form.email} onChange={set('email')} placeholder="amara@example.com" />
              </Field>
              <Field label="Alternative phone">
                <Input value={form.alt_phone} onChange={set('alt_phone')} placeholder="+254700000000" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="ID type">
                <Select value={form.id_type} onChange={set('id_type')}>
                  {['National ID', 'Passport', 'Driving licence', 'Alien ID', 'Other']
                    .map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="ID number">
                <Input value={form.id_number} onChange={set('id_number')} placeholder="2288-4471" />
              </Field>
              <Field label="Date of birth">
                <Input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
              </Field>
            </div>
          </div>
        </div>

        <div>
          <p className="label mb-3">Employment</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Occupation">
              <Input value={form.occupation} onChange={set('occupation')} placeholder="Software developer" />
            </Field>
            <Field label="Employer">
              <Input value={form.employer} onChange={set('employer')} />
            </Field>
          </div>
        </div>

        <div>
          <p className="label mb-3">Emergency contact</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Name">
              <Input value={form.emergency_name} onChange={set('emergency_name')} />
            </Field>
            <Field label="Phone">
              <Input value={form.emergency_phone} onChange={set('emergency_phone')} placeholder="+254700000000" />
            </Field>
            <Field label="Relationship">
              <Input value={form.emergency_relation} onChange={set('emergency_relation')} placeholder="Sibling" />
            </Field>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Status">
            <Select value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </Select>
          </Field>
          <Field
            label="How they want to hear from you"
            hint="A tenant who has asked for email only is never texted, whichever template you pick."
          >
            <Select value={form.contact_preference} onChange={set('contact_preference')}>
              <option value="both">Texts and email</option>
              <option value="sms">Texts only</option>
              <option value="email">Email only</option>
              <option value="none">Do not contact</option>
            </Select>
          </Field>
        </div>

        <div className="space-y-2">
          <Checkbox
            checked={portalEnabled}
            onChange={(e) => setPortalEnabled(e.target.checked)}
            label="Portal access"
            description="Allows them to sign in with their phone number to see what they owe, send proof of payment, upload documents and report repairs"
          />
          {portalEnabled && (
            <Checkbox
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              label={tenant ? 'Send them the invitation now' : 'Invite them now'}
              description="Sends the link and tells them to sign in with this phone number. Turn it off if you are recording a past tenancy."
            />
          )}
        </div>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} placeholder="Anything worth remembering" />
        </Field>
      </div>
    </Modal>
  );
}
